import React, { useState, useEffect, useCallback, useMemo } from 'react';
import aiDebugService from '../services/aiDebugService';
import cacheService from '../services/cacheService';
import yahooFinanceService from '../services/yahooFinanceService';

const CompanyTag = ({ company, stockData, item, customPrompts, analyzeWithAI }) => {
  const [showMenu, setShowMenu] = useState(false);

  const handlePromptSelect = (prompt) => {
    analyzeWithAI(item, prompt, company);
    setShowMenu(false);
  };

  const formatMarketCap = (marketCap) => {
    if (!marketCap) return 'N/A';
    if (marketCap >= 1e12) return `$${(marketCap / 1e12).toFixed(2)}T`;
    if (marketCap >= 1e9) return `$${(marketCap / 1e9).toFixed(2)}B`;
    if (marketCap >= 1e6) return `$${(marketCap / 1e6).toFixed(2)}M`;
    return `$${marketCap.toFixed(0)}`;
  };

  const formatChange = (change) => {
    if (!change) return '';
    const isPositive = change > 0;
    return (
      <span className={`daily-change ${isPositive ? 'positive' : 'negative'}`}>
        {isPositive ? '+' : ''}{change.toFixed(2)}%
      </span>
    );
  };

  return (
    <div className="company-stock-card">
      <div className="stock-card-header" onClick={() => setShowMenu(!showMenu)}>
        <div className="company-main-info">
          {stockData?.logo && (
            <img 
              src={stockData.logo} 
              alt={`${company} logo`}
              className="company-logo"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          )}
          <div className="company-text">
            <span className="company-symbol">{company}</span>
            {stockData?.name && (
              <span className="company-name">{stockData.name}</span>
            )}
          </div>
        </div>
        {stockData?.stock_price_usd && (
          <div className="stock-price-main">
            <span className="stock-price">${stockData.stock_price_usd.toFixed(2)}</span>
            {formatChange(stockData.daily_change_percent)}
          </div>
        )}
      </div>

      {stockData && (
        <div className="stock-details">
          <div className="stock-row">
            <div className="stock-metric">
              <span className="metric-label">Market Cap:</span>
              <span className="metric-value">
                {stockData.market_cap_usd ? formatMarketCap(stockData.market_cap_usd) : 'N/A'}
              </span>
            </div>
            <div className="stock-metric">
              <span className="metric-label">Day Range:</span>
              <span className="metric-value">
                {stockData.dayRange || (stockData.low && stockData.high) ? `${stockData.low?.toFixed(2)} - ${stockData.high?.toFixed(2)}` : 'N/A'}
              </span>
            </div>
          </div>
          
          <div className="stock-row">
            <div className="stock-metric">
              <span className="metric-label">Sector:</span>
              <span className="metric-value">{stockData.sector || 'N/A'}</span>
            </div>
            <div className="stock-metric">
              <span className="metric-label">Volume:</span>
              <span className="metric-value">
                {stockData.volume ? stockData.volume.toLocaleString() : 'N/A'}
              </span>
            </div>
          </div>
          
          {(stockData.dividend_yield_percent || stockData.consensus_price_target) && (
            <div className="stock-row">
              {stockData.dividend_yield_percent && (
                <div className="stock-metric">
                  <span className="metric-label">Dividend:</span>
                  <span className="metric-value">{stockData.dividend_yield_percent.toFixed(2)}%</span>
                </div>
              )}
              {stockData.consensus_price_target && (
                <div className="stock-metric">
                  <span className="metric-label">Target:</span>
                  <span className="metric-value">${stockData.consensus_price_target.toFixed(2)}</span>
                </div>
              )}
            </div>
          )}
          
          {/* External Stock Viewing Buttons */}
          <div className="external-links-section">
            <div className="external-links-label">View on:</div>
            <div className="external-links">
              <a 
                href={`https://finance.yahoo.com/quote/${company}`}
                target="_blank"
                rel="noopener noreferrer"
                className="external-link yahoo"
                title="View on Yahoo Finance"
              >
                <svg className="external-icon" viewBox="0 0 24 24" width="16" height="16">
                  <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                </svg>
                <span>Yahoo</span>
              </a>
              
              <a 
                href={`https://www.nasdaq.com/market-activity/stocks/${company}`}
                target="_blank"
                rel="noopener noreferrer"
                className="external-link nasdaq"
                title="View on Nasdaq"
              >
                <svg className="external-icon" viewBox="0 0 24 24" width="16" height="16">
                  <path fill="currentColor" d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
                <span>Nasdaq</span>
              </a>
              
              <a 
                href={`https://www.tradingview.com/symbols/${company}`}
                target="_blank"
                rel="noopener noreferrer"
                className="external-link tradingview"
                title="View on TradingView"
              >
                <svg className="external-icon" viewBox="0 0 24 24" width="16" height="16">
                  <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
                <span>TradingView</span>
              </a>
            </div>
          </div>
        </div>
      )}
      
      {showMenu && (
        <div className="company-prompt-menu">
          <div className="prompt-menu-header">
            <span>Select AI Analysis for {company}</span>
            <button 
              className="close-menu-button"
              onClick={() => setShowMenu(false)}
            >
              ✕
            </button>
          </div>
          <div className="prompt-options">
            {customPrompts.map((prompt, idx) => (
              <button
                key={idx}
                className="prompt-option"
                onClick={() => handlePromptSelect(prompt.prompt)}
              >
                {prompt.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const NewsFeeds = ({ refreshInterval, rssFeeds, openaiApiKey, finnhubApiKey, finnhubEnabled, yahooFinanceEnabled, customPrompts, masterPrompt }) => {
  console.log('NewsFeeds: Received props - openaiApiKey:', openaiApiKey ? 'Present' : 'Missing', 'finnhubApiKey:', finnhubApiKey ? 'Present' : 'Missing', 'yahooFinanceEnabled:', yahooFinanceEnabled);
  const [allNewsItems, setAllNewsItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [activeFeed, setActiveFeed] = useState('all');
  const [articleCount, setArticleCount] = useState(10);
  const [analyzingItems, setAnalyzingItems] = useState(new Set());
  const [aiResponses, setAiResponses] = useState({});
  const [stockPrices, setStockPrices] = useState({});
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Company detection patterns
  const companyPatterns = useMemo(() => ({
    'AAPL': ['Apple', 'iPhone', 'iPad', 'Mac', 'iOS', 'App Store', 'Tim Cook'],
    'MSFT': ['Microsoft', 'Windows', 'Office', 'Azure', 'Xbox', 'Satya Nadella'],
    'GOOGL': ['Google', 'Alphabet', 'YouTube', 'Android', 'Chrome', 'Sundar Pichai'],
    'AMZN': ['Amazon', 'AWS', 'Prime', 'Jeff Bezos', 'Andy Jassy'],
    'TSLA': ['Tesla', 'Elon Musk', 'Model S', 'Model 3', 'Model X', 'Model Y', 'Cybertruck'],
    'META': ['Meta', 'Facebook', 'Instagram', 'WhatsApp', 'Mark Zuckerberg', 'Reality Labs'],
    'NVDA': ['NVIDIA', 'Nvidia', 'GPU', 'AI chips', 'Jensen Huang', 'RTX'],
    'NFLX': ['Netflix', 'streaming', 'Reed Hastings', 'Ted Sarandos'],
    'AMD': ['AMD', 'Advanced Micro Devices', 'Lisa Su', 'Ryzen', 'Radeon'],
    'INTC': ['Intel', 'Pat Gelsinger', 'x86', 'processors'],
    'CRM': ['Salesforce', 'Marc Benioff', 'CRM', 'cloud software'],
    'ORCL': ['Oracle', 'Larry Ellison', 'database', 'cloud infrastructure'],
    'ADBE': ['Adobe', 'Creative Cloud', 'Photoshop', 'Shantanu Narayen'],
    'PYPL': ['PayPal', 'digital payments', 'Dan Schulman'],
    'UBER': ['Uber', 'ride-sharing', 'Dara Khosrowshahi'],
    'LYFT': ['Lyft', 'ride-sharing', 'Logan Green'],
    'SNAP': ['Snapchat', 'Snap', 'Evan Spiegel'],
    'SQ': ['Square', 'Jack Dorsey', 'Cash App', 'Block'],
    'ROKU': ['Roku', 'streaming device', 'Anthony Wood'],
    'ZM': ['Zoom', 'video conferencing', 'Eric Yuan'],
    'DOCU': ['DocuSign', 'electronic signature', 'Dan Springer'],
    'SNOW': ['Snowflake', 'data cloud', 'Frank Slootman'],
    'PLTR': ['Palantir', 'data analytics', 'Alex Karp'],
    'COIN': ['Coinbase', 'cryptocurrency', 'Brian Armstrong'],
    'HOOD': ['Robinhood', 'trading app', 'Vlad Tenev'],
    'RBLX': ['Roblox', 'gaming platform', 'David Baszucki'],
    'U': ['Unity', 'game engine', 'John Riccitiello'],
    'DDOG': ['Datadog', 'monitoring', 'Olivier Pomel'],
    'NET': ['Cloudflare', 'CDN', 'Matthew Prince'],
    'OKTA': ['Okta', 'identity management', 'Todd McKinnon'],
    'CRWD': ['CrowdStrike', 'cybersecurity', 'George Kurtz'],
    'ZS': ['Zscaler', 'cloud security', 'Jay Chaudhry'],
    'PANW': ['Palo Alto Networks', 'cybersecurity', 'Nikesh Arora'],
    'FTNT': ['Fortinet', 'cybersecurity', 'Ken Xie'],
    'CYBR': ['CyberArk', 'identity security', 'Udi Mokady'],
    'MDB': ['MongoDB', 'database', 'Dev Ittycheria'],
    'ESTC': ['Elastic', 'search engine', 'Shay Banon'],
    'SPLK': ['Splunk', 'data analytics', 'Gary Steele'],
    'WDAY': ['Workday', 'HR software', 'Aneel Bhusri'],
    'TEAM': ['Atlassian', 'Jira', 'Confluence', 'Mike Cannon-Brookes'],
    'WORK': ['Slack', 'workplace communication', 'Stewart Butterfield'],
    // Add missing companies
    'BA': ['Boeing', 'Boeing Co', 'Boeing Company', 'aircraft', 'airplane', '737', '787', '777'],
    'HON': ['Honeywell', 'Honeywell International', 'aerospace', 'automation', 'safety'],
    'AXP': ['American Express', 'Amex', 'credit card', 'platinum card'],
    'JPM': ['JPMorgan', 'JPMorgan Chase', 'Chase', 'Jamie Dimon', 'banking'],
    'BAC': ['Bank of America', 'BofA', 'banking', 'financial services'],
    'WFC': ['Wells Fargo', 'banking', 'financial services'],
    'GS': ['Goldman Sachs', 'investment banking', 'Lloyd Blankfein'],
    'MS': ['Morgan Stanley', 'investment banking', 'James Gorman'],
    'C': ['Citigroup', 'Citi', 'banking', 'Jane Fraser'],
    'JNJ': ['Johnson & Johnson', 'J&J', 'pharmaceuticals', 'healthcare'],
    'PFE': ['Pfizer', 'pharmaceuticals', 'vaccine', 'healthcare'],
    'UNH': ['UnitedHealth', 'UnitedHealth Group', 'healthcare', 'insurance'],
    'ABBV': ['AbbVie', 'pharmaceuticals', 'healthcare'],
    'MRK': ['Merck', 'pharmaceuticals', 'healthcare'],
    'TMO': ['Thermo Fisher', 'Thermo Fisher Scientific', 'healthcare', 'scientific'],
    'ABT': ['Abbott', 'Abbott Laboratories', 'healthcare', 'medical devices'],
    'DHR': ['Danaher', 'healthcare', 'life sciences'],
    'BMY': ['Bristol Myers', 'Bristol Myers Squibb', 'pharmaceuticals'],
    'AMGN': ['Amgen', 'biotechnology', 'pharmaceuticals'],
    'GILD': ['Gilead', 'Gilead Sciences', 'pharmaceuticals', 'biotechnology'],
    'LLY': ['Eli Lilly', 'pharmaceuticals', 'healthcare'],
    'V': ['Visa', 'payment processing', 'credit cards'],
    'MA': ['Mastercard', 'payment processing', 'credit cards'],
    'DIS': ['Disney', 'Walt Disney', 'entertainment', 'streaming', 'Bob Iger'],
    'CMCSA': ['Comcast', 'cable', 'internet', 'NBCUniversal'],
    'T': ['AT&T', 'telecommunications', 'wireless'],
    'VZ': ['Verizon', 'telecommunications', 'wireless'],
    'KO': ['Coca-Cola', 'beverages', 'soft drinks'],
    'PEP': ['PepsiCo', 'Pepsi', 'beverages', 'snacks'],
    'WMT': ['Walmart', 'retail', 'grocery', 'Doug McMillon'],
    'HD': ['Home Depot', 'retail', 'home improvement'],
    'LOW': ['Lowe\'s', 'retail', 'home improvement'],
    'TGT': ['Target', 'retail', 'grocery'],
    'COST': ['Costco', 'retail', 'wholesale', 'membership'],
    'PG': ['Procter & Gamble', 'P&G', 'consumer goods', 'household products'],
    'NKE': ['Nike', 'athletic wear', 'sports', 'sneakers'],
    'MCD': ['McDonald\'s', 'fast food', 'restaurants'],
    'SBUX': ['Starbucks', 'coffee', 'restaurants', 'Howard Schultz'],
    'YUM': ['Yum Brands', 'KFC', 'Pizza Hut', 'Taco Bell', 'restaurants'],
    'CMG': ['Chipotle', 'Mexican food', 'restaurants'],
    'TMUS': ['T-Mobile', 'telecommunications', 'wireless'],
    'CHTR': ['Charter', 'Spectrum', 'cable', 'internet'],
    'DISH': ['Dish Network', 'satellite', 'television'],
    'SIRI': ['SiriusXM', 'satellite radio', 'entertainment'],
    'SPOT': ['Spotify', 'music streaming', 'entertainment'],
    'EBAY': ['eBay', 'e-commerce', 'online marketplace'],
    'SHOP': ['Shopify', 'e-commerce', 'online stores'],
    'ETSY': ['Etsy', 'e-commerce', 'handmade', 'vintage'],
    'W': ['Wayfair', 'e-commerce', 'home goods'],
    'CHWY': ['Chewy', 'e-commerce', 'pet supplies'],
    'SAP': ['SAP', 'enterprise software', 'business software'],
    'INTU': ['Intuit', 'QuickBooks', 'TurboTax', 'financial software'],
    'NOW': ['ServiceNow', 'IT service management', 'cloud software'],
    'SAIL': ['SailPoint', 'identity governance', 'access management'],
    'TENB': ['Tenable', 'vulnerability management', 'cybersecurity'],
    'QLYS': ['Qualys', 'vulnerability management', 'compliance'],
    'RPD': ['Rapid7', 'vulnerability management', 'security analytics'],
    'FEYE': ['FireEye', 'cybersecurity', 'threat intelligence'],
    'IBM': ['IBM', 'International Business Machines', 'Watson', 'cloud computing'],
    'HPE': ['Hewlett Packard Enterprise', 'HPE', 'servers', 'storage'],
    'DELL': ['Dell Technologies', 'Dell', 'computers', 'servers'],
    'CSCO': ['Cisco', 'networking', 'routers', 'switches'],
    'JNPR': ['Juniper Networks', 'networking', 'routers', 'switches'],
    'ARISTA': ['Arista Networks', 'networking', 'switches'],
    'FFIV': ['F5 Networks', 'application delivery', 'load balancing'],
    'NTAP': ['NetApp', 'storage', 'data management'],
    'WDC': ['Western Digital', 'hard drives', 'storage'],
    'STX': ['Seagate', 'hard drives', 'storage'],
    'MU': ['Micron', 'memory', 'semiconductors'],
    'QCOM': ['Qualcomm', 'mobile chips', '5G', 'semiconductors'],
    'AVGO': ['Broadcom', 'semiconductors', 'networking chips'],
    'TXN': ['Texas Instruments', 'TI', 'semiconductors', 'analog chips'],
    'ADI': ['Analog Devices', 'semiconductors', 'analog chips'],
    'MCHP': ['Microchip', 'microcontrollers', 'semiconductors'],
    'ON': ['ON Semiconductor', 'semiconductors', 'power management'],
    'SWKS': ['Skyworks', 'RF chips', 'semiconductors'],
    'QRVO': ['Qorvo', 'RF chips', 'semiconductors'],
    'MRVL': ['Marvell', 'semiconductors', 'networking chips'],
    'AMAT': ['Applied Materials', 'semiconductor equipment', 'manufacturing'],
    'LRCX': ['Lam Research', 'semiconductor equipment', 'manufacturing'],
    'KLAC': ['KLA', 'semiconductor equipment', 'inspection'],
    'TER': ['Teradyne', 'semiconductor testing', 'equipment'],
    'COHU': ['Cohu', 'semiconductor testing', 'equipment'],
    'FORM': ['FormFactor', 'semiconductor testing', 'equipment'],
    'ONTO': ['Onto Innovation', 'semiconductor equipment', 'metrology'],
    'PLAB': ['Photronics', 'semiconductor equipment', 'photomask'],
    'UCTT': ['Ultra Clean', 'semiconductor equipment', 'cleaning'],
    'ACMR': ['ACM Research', 'semiconductor equipment', 'cleaning'],
    'ICHR': ['Ichor Systems', 'semiconductor equipment', 'fluid delivery']
  }), []);

  const detectCompanyPatterns = useCallback((title, content) => {
    const text = `${title} ${content}`.toLowerCase();
    const detectedCompanies = [];
    
    for (const [symbol, patterns] of Object.entries(companyPatterns)) {
      for (const pattern of patterns) {
        if (text.includes(pattern.toLowerCase())) {
          detectedCompanies.push(symbol);
          break;
        }
      }
    }
    
    return [...new Set(detectedCompanies)]; // Remove duplicates
  }, [companyPatterns]);

  // Fetch stock data from available services (Yahoo Finance or FinHub)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchStockData = async (symbol) => {
    console.log(`NewsFeeds: fetchStockData called for ${symbol}`);
    console.log(`NewsFeeds: yahooFinanceEnabled: ${yahooFinanceEnabled}, finnhubEnabled: ${finnhubEnabled}, finnhubApiKey: ${finnhubApiKey ? 'Present' : 'Missing'}`);
    
    // Check cache first
    const cachedData = cacheService.getCachedStockData(symbol);
    if (cachedData) {
      console.log(`NewsFeeds: Using cached data for ${symbol}`);
      return cachedData;
    }
    
    // Use Yahoo Finance 2 if enabled (preferred)
    if (yahooFinanceEnabled) {
      try {
        console.log(`NewsFeeds: Fetching stock data for ${symbol} using Yahoo Finance 2`);
        const stockData = await yahooFinanceService.getQuote(symbol);
        if (stockData) {
          // Cache the result
          cacheService.cacheStockData(symbol, stockData);
          return stockData;
        }
      } catch (error) {
        console.error(`NewsFeeds: Yahoo Finance 2 failed for ${symbol}:`, error);
      }
    }

    // Use FinHub as fallback if enabled and Yahoo Finance is not enabled
    if (finnhubEnabled && finnhubApiKey && !yahooFinanceEnabled) {
      console.log(`NewsFeeds: Fetching stock data for ${symbol} using FinHub`);
      return await fetchStockDataFromFinnhub(symbol);
    }

    console.log(`NewsFeeds: No stock data service available for ${symbol}`);
    return null;
  };

  // Fetch stock data from FinHub API (fallback)
  const fetchStockDataFromFinnhub = async (symbol) => {
    if (!finnhubEnabled || !finnhubApiKey) {
      return null;
    }

    // Check cache first
    const cachedData = cacheService.getCachedStockData(symbol);
    if (cachedData) {
      console.log(`NewsFeeds: Using cached FinHub data for ${symbol}`);
      return cachedData;
    }

    try {
      console.log(`NewsFeeds: Fetching fresh FinHub data for ${symbol}`);
      const response = await fetch(
        `https://finnhub.io/api/v1/quote?symbol=${symbol}&token=${finnhubApiKey}`
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (!data || data.c === 0) {
        console.log(`NewsFeeds: No valid data from FinHub for ${symbol}`);
        return null;
      }
      
      const stockData = {
        symbol: symbol,
        price: data.c,
        change: data.d,
        changePercent: data.dp,
        high: data.h,
        low: data.l,
        open: data.o,
        previousClose: data.pc,
        timestamp: Date.now()
      };
      
      // Cache the result
      cacheService.cacheStockData(symbol, stockData);
      
      return stockData;
    } catch (error) {
      console.error(`NewsFeeds: FinHub API error for ${symbol}:`, error);
      return null;
    }
  };

  const detectCompanyWithAI = useCallback(async (title, content) => {
    let detectedCompanies = [];

    // Check cache first for company detection
    const cachedCompanies = cacheService.getCachedCompanyDetection(title);
    if (cachedCompanies) {
      console.log('Using cached company detection for:', title);
      detectedCompanies = cachedCompanies;
    } else {
      // No cache, detect companies using AI or pattern matching
      if (!openaiApiKey) {
        console.log('NewsFeeds: No OpenAI API key available, using pattern matching');
        // Fallback to pattern matching if no API key
        const symbols = detectCompanyPatterns(title, content);
        detectedCompanies = symbols.map(symbol => ({ symbol, name: symbol }));
      } else {
        console.log('NewsFeeds: Using OpenAI API for company detection with key:', openaiApiKey.substring(0, 20) + '...');
        
        // Check if the API key looks valid (basic validation)
        if (openaiApiKey.length < 20 || !openaiApiKey.startsWith('sk-')) {
          console.log('NewsFeeds: API key appears invalid, using pattern matching');
          const symbols = detectCompanyPatterns(title, content);
          detectedCompanies = symbols.map(symbol => ({ symbol, name: symbol }));
        } else {
        try {
          // Add delay to prevent OpenAI rate limiting
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Use OpenAI for company detection with a specific prompt
          
          const response = await fetch('https://api.openai.com/v1/chat/completions', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${openaiApiKey}`
            },
            body: JSON.stringify({
              model: aiDebugService.getMasterPromptModel(),
              messages: [
                {
                  role: 'system',
                  content: 'You are a financial data extraction AI. Analyze news headlines to identify publicly traded companies. Return ONLY a JSON object with this exact format: {"companies": [{"symbol": "AAPL", "name": "Apple Inc."}]}. If no companies are found, return {"companies": []}.'
                },
                {
                  role: 'user',
                  content: `Headline: ${title}`
                }
              ],
              response_format: { type: 'json_object' },
              temperature: 0
            })
          });

          if (!response.ok) {
            const errorText = await response.text();
            console.error('OpenAI API Error Response (Company Detection):', errorText);
            throw new Error(`OpenAI API error: ${response.status} - ${errorText}`);
          }

          const data = await response.json();
          const aiResponse = data.choices[0].message.content;
          
          // Log debug information for company detection
          aiDebugService.logAiRequest(
            'company-detection',
            aiDebugService.getMasterPromptModel(),
            `Headline: ${title}`,
            aiResponse
          );
          
          try {
            const parsed = JSON.parse(aiResponse);
            
            if (parsed.companies && parsed.companies.length > 0) {
              // Cache the company detection result
              cacheService.cacheCompanyDetection(title, parsed.companies);
              detectedCompanies = parsed.companies;
            }
          } catch (parseError) {
            console.error('Error parsing AI response:', parseError, 'Raw response:', aiResponse);
            
            // Log error for debugging
            aiDebugService.logAiRequest(
              'company-detection',
              aiDebugService.getMasterPromptModel(),
              `Headline: ${title}`,
              null,
              `JSON Parse Error: ${parseError.message}`
            );
            
            // Fallback to pattern matching
            const symbols = detectCompanyPatterns(title, content);
            detectedCompanies = symbols.map(symbol => ({ symbol, name: symbol }));
          }
        } catch (err) {
          console.error('Error with AI company detection:', err);
          
          // Check if it's an API key error and provide helpful message
          if (err.message.includes('401') || err.message.includes('invalid_api_key')) {
            console.log('OpenAI API key is invalid, falling back to pattern matching');
          }
          
          // Log error for debugging
          aiDebugService.logAiRequest(
            'company-detection',
            aiDebugService.getMasterPromptModel(),
            `Headline: ${title}`,
            null,
            `API Error: ${err.message}`
          );
          
          // Fallback to pattern matching
          const symbols = detectCompanyPatterns(title, content);
          detectedCompanies = symbols.map(symbol => ({ symbol, name: symbol }));
        }
        }
      }
    }

    // Always fetch fresh stock data from FinHub for detected companies
    if (detectedCompanies.length > 0) {
      const companySymbols = [];
      const companyData = {};
      
      for (const company of detectedCompanies) {
        if (company.symbol) {
          companySymbols.push(company.symbol);
          
          // Fetch stock data from available services
          try {
            console.log(`NewsFeeds: About to fetch stock data for ${company.symbol}`);
            const stockData = await fetchStockData(company.symbol);
            console.log(`NewsFeeds: Received stock data for ${company.symbol}:`, stockData);
            if (stockData) {
              companyData[company.symbol] = {
                name: company.name || stockData.name || company.symbol,
                symbol: company.symbol,
                ...stockData
              };
            } else {
              // Fallback data if FinHub fails - use correct field names
              companyData[company.symbol] = {
                name: company.name || company.symbol,
                symbol: company.symbol,
                price: null,
                change: null,
                changePercent: null,
                open: null,
                high: null,
                low: null,
                volume: null,
                marketCap: null,
                pe: null,
                dividendYield: null,
                previousClose: null,
                currency: 'USD',
                exchange: 'Unknown',
                timezone: 'UTC',
                lastUpdate: new Date().toISOString(),
                sector: null,
                industry: null,
                logo: null,
                country: null,
                ipoDate: null,
                sharesOutstanding: null,
                dayRange: null,
                // Keep old field names for backward compatibility
                stock_price_usd: null,
                daily_change_percent: null,
                market_cap_usd: null,
                pe_ratio_ttm: null,
                dividend_yield_percent: null
              };
            }
          } catch (error) {
            console.error(`Error fetching FinHub data for ${company.symbol}:`, error);
            // Fallback data with correct field names
            companyData[company.symbol] = {
              name: company.name || company.symbol,
              symbol: company.symbol,
              price: null,
              change: null,
              changePercent: null,
              open: null,
              high: null,
              low: null,
              volume: null,
              marketCap: null,
              pe: null,
              dividendYield: null,
              previousClose: null,
              currency: 'USD',
              exchange: 'Unknown',
              timezone: 'UTC',
              lastUpdate: new Date().toISOString(),
              sector: null,
              industry: null,
              logo: null,
              country: null,
              ipoDate: null,
              sharesOutstanding: null,
              dayRange: null,
              // Keep old field names for backward compatibility
              stock_price_usd: null,
              daily_change_percent: null,
              market_cap_usd: null,
              pe_ratio_ttm: null,
              dividend_yield_percent: null
            };
          }
        }
      }
      
      // Update stock prices with FinHub data
      setStockPrices(prev => ({ ...prev, ...companyData }));
      return companySymbols;
    }

    return [];
  }, [openaiApiKey, detectCompanyPatterns, fetchStockData]); // eslint-disable-line react-hooks/exhaustive-deps, no-use-before-define

  // Fetch stock data from available services (Yahoo Finance or FinHub)


  const fetchStockPrice = useCallback(async (symbol) => {
    if (!finnhubApiKey) return null;
    
    // Add a small delay to prevent rate limiting
    await new Promise(resolve => setTimeout(resolve, 100));
    
    try {
      const response = await fetch(`https://finnhub.io/api/v1/quote?symbol=${symbol}&token=${finnhubApiKey}`);
      if (!response.ok) {
        if (response.status === 429) {
          console.warn(`Rate limited for ${symbol}, skipping...`);
          return null;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data.c; // Current price
    } catch (err) {
      console.error(`Error fetching stock price for ${symbol}:`, err);
      return null;
    }
  }, [finnhubApiKey]);

  const fetchStockPrices = useCallback(async (symbols) => {
    if (!finnhubEnabled || !finnhubApiKey || symbols.length === 0) return;
    
    // Process symbols sequentially to avoid rate limiting
    const priceMap = {};
    for (const symbol of symbols) {
      try {
        const price = await fetchStockPrice(symbol);
        if (price !== null) {
          priceMap[symbol] = { price };
        }
        // Add delay between requests to prevent rate limiting
        await new Promise(resolve => setTimeout(resolve, 200));
      } catch (error) {
        console.error(`Error processing ${symbol}:`, error);
      }
    }
    
    // Merge with existing data instead of overwriting
    setStockPrices(prev => {
      const merged = { ...prev };
      Object.keys(priceMap).forEach(symbol => {
        if (merged[symbol]) {
          // Preserve existing AI data and only update price
          merged[symbol] = { ...merged[symbol], price: priceMap[symbol].price };
        } else {
          // No existing data, use FinHub data
          merged[symbol] = priceMap[symbol];
        }
      });
      return merged;
    });
  }, [finnhubEnabled, finnhubApiKey, fetchStockPrice]);

  const parseRSSFeed = useCallback(async (xmlText, feedName, feedColor, maxItemsPerFeed = 10) => {
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlText, 'text/xml');
      
      // Check for parsing errors
      const parseError = xmlDoc.getElementsByTagName('parsererror')[0];
      if (parseError) {
        throw new Error('XML parsing error');
      }
      
      const items = xmlDoc.getElementsByTagName('item');
      const newsItems = [];
      
      for (let i = 0; i < Math.min(items.length, maxItemsPerFeed); i++) {
        const item = items[i];
        const title = item.getElementsByTagName('title')[0]?.textContent || 'No title';
        const link = item.getElementsByTagName('link')[0]?.textContent || '#';
        const pubDate = item.getElementsByTagName('pubDate')[0]?.textContent || new Date().toISOString();
        const description = item.getElementsByTagName('description')[0]?.textContent || '';
        const creator = item.getElementsByTagName('dc:creator')[0]?.textContent || feedName;
        
        // Detect companies using AI
        const detectedCompanies = await detectCompanyWithAI(title, description);
        
        // Skip articles that return ERR_NOT_FOUND (no companies detected)
        if (detectedCompanies === 'ERR_NOT_FOUND') {
          continue;
        }
        
        // Create a unique ID based on title and link to avoid duplicates
        const idString = title.trim() + link.trim();
        const encoded = encodeURIComponent(idString);
        const uniqueId = encoded.replace(/[^a-zA-Z0-9]/g, '').substring(0, 20);
        
        newsItems.push({
          id: uniqueId,
          title: title.trim(),
          link: link.trim(),
          pubDate: new Date(pubDate),
          content: description.trim(),
          source: creator.trim(),
          feedName: feedName,
          feedColor: feedColor,
          detectedCompanies: detectedCompanies
        });
      }
      
      return newsItems;
    } catch (err) {
      console.error(`Error parsing RSS feed ${feedName}:`, err);
      throw err;
    }
  }, [detectCompanyWithAI]);

  const fetchRSSFeed = useCallback(async (feed, maxItemsPerFeed = 10) => {
    try {
      console.log(`NewsFeeds: Fetching RSS feed ${feed.name} from ${feed.url}`);
      const proxyUrl = 'https://api.allorigins.win/get?url=';
      const fullUrl = proxyUrl + encodeURIComponent(feed.url);
      console.log(`NewsFeeds: Full proxy URL: ${fullUrl}`);
      
      const response = await fetch(fullUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log(`NewsFeeds: Received data for ${feed.name}:`, data);
      const xmlText = data.contents;
      
      const parsedItems = await parseRSSFeed(xmlText, feed.name, feed.color, maxItemsPerFeed);
      console.log(`NewsFeeds: Parsed ${parsedItems.length} items from ${feed.name}`);
      
      return parsedItems;
    } catch (err) {
      console.error(`Error fetching RSS feed ${feed.name}:`, err);
      return [];
    }
  }, [parseRSSFeed]);

  const fetchAllFeeds = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('NewsFeeds: fetchAllFeeds called with rssFeeds:', rssFeeds);
      const enabledFeeds = rssFeeds.filter(feed => feed.enabled);
      console.log('NewsFeeds: enabledFeeds after filter:', enabledFeeds);
      
      if (enabledFeeds.length === 0) {
        console.log('NewsFeeds: No enabled feeds found!');
        setError('No RSS feeds are enabled. Please enable at least one feed in Settings.');
        setLoading(false);
        return;
      }
      
      // Calculate items per feed to get approximately the desired total
      const itemsPerFeed = Math.ceil(articleCount / enabledFeeds.length);
      const allPromises = enabledFeeds.map(feed => fetchRSSFeed(feed, itemsPerFeed));
      
      const results = await Promise.all(allPromises);
      const allItems = results.flat();
      
      // Remove duplicates based on unique ID
      const uniqueItems = [];
      const seenIds = new Set();
      
      for (const item of allItems) {
        if (!seenIds.has(item.id)) {
          seenIds.add(item.id);
          uniqueItems.push(item);
        }
      }
      
      // Sort by publication date (newest first)
      uniqueItems.sort((a, b) => new Date(b.pubDate) - new Date(a.pubDate));
      
      // Limit to the exact article count requested
      const limitedItems = uniqueItems.slice(0, articleCount);
      
      setAllNewsItems(limitedItems);
      setLastUpdated(new Date());
      
      // Fetch stock prices for all detected companies (limit to prevent rate limiting)
      const allDetectedCompanies = [...new Set(limitedItems.flatMap(item => item.detectedCompanies))];
      if (allDetectedCompanies.length > 0) {
        // Limit to first 10 companies to prevent rate limiting
        const limitedCompanies = allDetectedCompanies.slice(0, 10);
        console.log(`NewsFeeds: Fetching stock prices for ${limitedCompanies.length} companies (limited from ${allDetectedCompanies.length})`);
        fetchStockPrices(limitedCompanies);
      }
    } catch (err) {
      console.error('Error fetching RSS feeds:', err);
      setError('Failed to fetch news feeds. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [rssFeeds, articleCount, fetchStockPrices, fetchRSSFeed]);

  const analyzeWithAI = async (item, prompt, selectedSymbol = null) => {
    if (!openaiApiKey) {
      setError('Please configure your OpenAI API key in Settings first.');
      return;
    }

    const itemId = item.id;
    setAnalyzingItems(prev => new Set([...prev, itemId]));

    // Use the selected symbol or the first detected company
    const symbol = selectedSymbol || (item.detectedCompanies && item.detectedCompanies[0]) || 'UNKNOWN';
    
    // Replace placeholders in the prompt
    const processedPrompt = prompt
      .replace('{SYMBOL}', symbol)
      .replace('{COMPANY}', symbol);

    // Determine which model to use based on prompt type
    const isMasterPrompt = prompt.includes('{COMPANY}') && prompt.includes('financial analyst');
    const selectedModel = isMasterPrompt ? 
      aiDebugService.getMasterPromptModel() : 
      aiDebugService.getCustomPromptModel();

    try {

      const requestBody = {
        model: selectedModel,
        messages: [
          {
            role: 'system',
            content: 'You are a financial analyst AI assistant. Provide detailed, accurate, and professional analysis. Always include disclaimers that this is not financial advice.'
          },
          {
            role: 'user',
            content: `${processedPrompt}\n\nNews Headline: ${item.title}\nNews Content: ${item.content}`
          }
        ],
        max_tokens: 1000,
        temperature: 0.7
      };

      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${openaiApiKey}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('OpenAI API Error Response (Analysis):', errorText);
        throw new Error(`OpenAI API error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      const aiResponse = data.choices[0].message.content;
      
      // Log debug information
      const debugType = isMasterPrompt ? 'master-prompt' : 'custom-prompt';
      aiDebugService.logAiRequest(
        debugType,
        selectedModel,
        `${processedPrompt}\n\nNews Headline: ${item.title}\nNews Content: ${item.content}`,
        aiResponse
      );
      
      setAiResponses(prev => ({
        ...prev,
        [itemId]: aiResponse
      }));
    } catch (err) {
      console.error('Error calling OpenAI API:', err);
      
      // Log error for debugging
      const debugType = isMasterPrompt ? 'master-prompt' : 'custom-prompt';
      aiDebugService.logAiRequest(
        debugType,
        selectedModel,
        `${processedPrompt}\n\nNews Headline: ${item.title}\nNews Content: ${item.content}`,
        null,
        err.message
      );
      
      setError(`Failed to get AI analysis: ${err.message}`);
    } finally {
      setAnalyzingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
    }
  };

  useEffect(() => {
    console.log('NewsFeeds: useEffect triggered with rssFeeds:', rssFeeds, 'refreshInterval:', refreshInterval);
    fetchAllFeeds(); // Initial fetch
    
    if (!autoRefresh) return;
    
    const interval = setInterval(fetchAllFeeds, refreshInterval * 1000);
    
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshInterval, rssFeeds, articleCount, autoRefresh]); // Removed fetchAllFeeds from dependencies to prevent infinite loop

  const formatDate = (date) => {
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const openInNewTab = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };


  const getFilteredNews = () => {
    if (activeFeed === 'all') {
      return allNewsItems;
    }
    return allNewsItems.filter(item => item.feedName === activeFeed);
  };

  const getUniqueFeedNames = () => {
    const feedNames = [...new Set(allNewsItems.map(item => item.feedName))];
    return feedNames.sort();
  };

  return (
    <div className="page-content">
      <div className="finance-source">
        <div className="finance-header">
          <h2>News Feeds</h2>
          <p>Latest financial news and market updates with AI analysis</p>
          {lastUpdated && (
            <div className="last-updated">
              Last updated: {formatDate(lastUpdated)}
            </div>
          )}
        </div>
        
        <div className="finance-content">
          {loading && allNewsItems.length === 0 ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading news feeds...</p>
            </div>
          ) : error ? (
            <div className="error-container">
              <h3>⚠️ Error Loading News</h3>
              <p>{error}</p>
              <button 
                className="retry-button"
                onClick={fetchAllFeeds}
              >
                Retry
              </button>
            </div>
          ) : (
            <div className="news-container">
              <div className="news-header">
                <h3>Latest Financial News</h3>
                <div className="news-controls">
                  <div className="feed-filter">
                    <label htmlFor="feed-select">Filter by source:</label>
                    <select 
                      id="feed-select"
                      value={activeFeed}
                      onChange={(e) => setActiveFeed(e.target.value)}
                      className="feed-select"
                    >
                      <option value="all">All Sources</option>
                      {getUniqueFeedNames().map(feedName => (
                        <option key={feedName} value={feedName}>{feedName}</option>
                      ))}
                    </select>
                  </div>
                  <div className="article-count-filter">
                    <label htmlFor="article-count-select">Articles to load:</label>
                    <select 
                      id="article-count-select"
                      value={articleCount}
                      onChange={(e) => setArticleCount(parseInt(e.target.value))}
                      className="feed-select"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={30}>30</option>
                      <option value={50}>50</option>
                    </select>
                  </div>
                  <div className="refresh-controls">
                    <label className="auto-refresh-toggle">
                      <input 
                        type="checkbox" 
                        checked={autoRefresh}
                        onChange={(e) => setAutoRefresh(e.target.checked)}
                      />
                      Auto-refresh
                    </label>
                    <button 
                      className="refresh-button"
                      onClick={fetchAllFeeds}
                      disabled={loading}
                    >
                      {loading ? 'Refreshing...' : 'Refresh Now'}
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="news-list">
                {getFilteredNews().map((item, index) => (
                  <div key={item.id || index} className="news-item">
                    <div className="news-item-header">
                      <h4 
                        className="news-title"
                        onClick={() => openInNewTab(item.link)}
                      >
                        {item.title}
                      </h4>
                      <div className="news-item-meta">
                        <span className="news-date">{formatDate(item.pubDate)}</span>
                        <button 
                          className="read-more-button"
                          onClick={() => openInNewTab(item.link)}
                        >
                          READ MORE →
                        </button>
                      </div>
                    </div>
                    
                    <div className="news-source-section">
                      <span className="news-source-tag" style={{ backgroundColor: item.feedColor || '#dc2626' }}>{item.feedName}</span>
                    </div>
                    
                    {item.detectedCompanies.length > 0 && (
                      <div className="company-tags-section">
                        <div className="company-tags">
                          {item.detectedCompanies.map(company => (
                            <CompanyTag 
                              key={company} 
                              company={company} 
                              stockData={stockPrices[company]} 
                              item={item}
                              customPrompts={customPrompts}
                              analyzeWithAI={analyzeWithAI}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {analyzingItems.has(item.id) && (
                      <div className="ai-loading">
                        <div className="ai-spinner"></div>
                        <span>Analyzing with AI...</span>
                      </div>
                    )}
                    
                    {aiResponses[item.id] && (
                      <div className="ai-response-content">
                        <div className="ai-response-header">
                          <span className="ai-response-title">AI Analysis Result</span>
                          <button 
                            className="close-ai-response"
                            onClick={() => {
                              setAiResponses(prev => {
                                const newResponses = { ...prev };
                                delete newResponses[item.id];
                                return newResponses;
                              });
                            }}
                          >
                            ✕
                          </button>
                        </div>
                        <div className="ai-response-text">
                          {aiResponses[item.id].split('\n').map((paragraph, idx) => (
                            <p key={idx}>{paragraph}</p>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewsFeeds;
